import type { LocationQuery, RouteLocationRaw, Router } from 'vue-router';

const HOME = '/';

// Only used as a base to parse against; never navigated to.
const PLACEHOLDER_ORIGIN = 'https://login-redirect.invalid';

// Pages that make no sense to arrive at straight after signing in: they would sign the
// user back out, or exist only to say there is nothing here.
const NON_LANDING_ROUTES = new Set(['Logout', 'Not Found', 'CatchAll']);

// A single leading slash, so neither a protocol-relative URL (`//evil.com`) nor the
// backslash form browsers normalise into one (`/\evil.com`).
const SINGLE_LEADING_SLASH = /^\/(?![/\\])/;

// Browsers strip tab and newline from URLs, which turns `/\t/evil.com` into `//evil.com`
// after this check has passed; a legitimate path arrives percent-encoded and has none.
const UNSAFE_CHARACTERS = /[\\@\s]/;

function HasControlCharacter(path: string): boolean {
  return [...path].some((char) => char.charCodeAt(0) < 0x20 || char.charCodeAt(0) === 0x7f);
}

export function IsSafeRelativePath(path: string): boolean {
  return SINGLE_LEADING_SLASH.test(path) && !UNSAFE_CHARACTERS.test(path) && !HasControlCharacter(path);
}

function AllowedQuery(query: LocationQuery, allowedKeys: readonly string[]): LocationQuery {
  return Object.fromEntries(Object.entries(query).filter(([key]) => allowedKeys.includes(key)));
}

/**
 * Turns the path captured before the Cognito redirect into somewhere safe to land.
 *
 * The path round-trips through the identity provider as OAuth `customState`, so it is
 * untrusted: anything that is not a relative path to a known route falls back to home,
 * and only the query parameters the destination route declares in `meta.deepLinkQuery`
 * survive.
 */
export function ResolveLoginRedirect(router: Router, returnPath: unknown): RouteLocationRaw {
  if (typeof returnPath !== 'string' || !IsSafeRelativePath(returnPath)) {
    return HOME;
  }

  let url: URL;
  try {
    url = new URL(returnPath, PLACEHOLDER_ORIGIN);
  } catch {
    return HOME;
  }
  if (url.origin !== PLACEHOLDER_ORIGIN) {
    return HOME;
  }

  const route = router.resolve(`${url.pathname}${url.search}`);
  const leaf = route.matched.at(-1);
  if (!leaf || leaf.redirect || route.meta.public || NON_LANDING_ROUTES.has(String(route.name))) {
    return HOME;
  }

  return { path: route.path, query: AllowedQuery(route.query, route.meta.deepLinkQuery ?? []) };
}
